<script>
import { mapGetters } from 'vuex';
import {
  plan, remove,
} from '@/api/plan';
import ModernInput from '@/components/ModernInput.vue';
import ReportsTableTooltip from '@/components/Widgets/ReportsTableTooltip.vue';
import ModernTable from '@/components/ModernTable.vue';
import ModernSelect from '@/components/ModernSelect.vue';
import PlansCustomerForm from '@/views/Manage/Plans/PlansCustomerForm.vue';
import PlanCustomer from '@/entity/PlanCustomer';
import { get } from 'lodash';

export default {
  name: 'Plans',
  components: {
    ModernSelect, ModernTable, ReportsTableTooltip, ModernInput,
  },

  data() {
    return {
      columns: [
        {
          name: '',
          label: '',
          width: 0,
          centered: true,
          cellClass: 'cell-costil',
          headerClass: 'costil',
        },
        {
          name: 'id', label: 'ID', width: 100, centered: true,
        },
        { name: 'name', label: 'Name', width: 100 },
        { name: 'discount', label: 'Stripe ID', width: 250 },
        {
          name: 'commission', label: 'Price', width: 150, centered: true,
        },
        { name: 'stripeId', label: 'Associated Stripe ID', width: 250 },
        { name: 'iic', label: 'Installment Intervals Count', width: 200 },
        { name: 'opportunities', label: 'Opportunities', width: 150 },
        { name: 'tier', label: 'Tier', width: 80 },
        { name: 'description', label: 'Description', width: 200 },
        { name: '', label: '', width: 100 },
      ],
      data: [],
      loading: false,
      total: 0,
      perPage: 25,
      page: 1,
      sortField: 'id',
      sortOrder: 'desc',
      defaultSort: 'id',
      defaultSortDirection: 'desc',
      searchText: null,
      perPageOptions: [{ value: 10 },
        { value: 25 }, { value: 50 }, { value: 100 }],
      PLAN_MODIFY: { resource: 'manage-plans', code: 'modify' },
    };
  },

  mounted() {
    this.loadAsyncData();
  },

  activated() {
    this.loadAsyncData();
  },

  computed: {
    ...mapGetters(['authAuthenticator']),
  },

  methods: {
    async doSearch() {
      this.page = 1;
      await this.loadAsyncData();
    },

    async loadAsyncData() {
      this.loading = true;
      const queryParams = {
        page: this.page,
        limit: this.perPage,
        order: `${this.sortOrder === 'desc' ? '-' : '+'}${this.sortField}`,
        search: this.searchText ? this.searchText.trim() : null,
      };

      try {
        const { data } = await plan(queryParams);
        this.total = data.total;
        this.data = data.data.map(
          (planCustomer) => new PlanCustomer().loadData(planCustomer),
        );
      } catch (error) {
        this.$buefy.snackbar.open({
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    async onPageChangeHandler(page) {
      this.page = page;
      await this.loadAsyncData();
    },

    async perPageChange() {
      this.page = 1;
      await this.loadAsyncData();
    },

    async onSortHandler(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      await this.loadAsyncData();
    },

    /**
     * Remove plan
     */
    onRemoveHandler(selectedPlan) {
      this.$buefy.dialog.confirm({
        title: 'Deleting plan',
        message: 'Are you sure you want to <b>delete</b> this plan? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        onConfirm: async () => {
          try {
            this.loading = true;
            await remove(selectedPlan);
            await this.loadAsyncData();
          } catch (error) {
            this.$buefy.snackbar.open({
              type: 'is-danger',
              message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
              position: 'is-top',
              indefinite: true,
              actionText: ' ',
            });
          } finally {
            this.loading = false;
          }
        },
      });
    },

    async onAddHandler() {
      const self = this;
      this.$buefy.modal.open({
        parent: this,
        component: PlansCustomerForm,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'modern-fix-modal',
        canCancel: ['outside'],
        events: {
          async submit() {
            await self.loadAsyncData();
          },
        },
      });
    },

    /**
     * @param {PlanCustomer} planCustomer
     */
    async onEditHandler(planCustomer) {
      const self = this;
      await this.$buefy.modal.open({
        parent: this,
        component: PlansCustomerForm,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'modern-fix-modal',
        canCancel: ['outside'],
        props: {
          plan: Object.assign(new PlanCustomer(), planCustomer),
        },
        events: {
          async submit() {
            await self.loadAsyncData();
          },
        },
      });
    },
  },

};
</script>

<template>
  <div class="grid-module" ref="module">
    <div class="columns">
      <div class="column is-2 btn-flex sm-show" style="display: none; min-width: 180px; gap: .75em">
        <b-button
          v-if="authAuthenticator(PLAN_MODIFY)"
          class="button-right modern-button"
          type="outlined"
          position="is-right"
          style="max-width: 160px; width: 160px; margin-top: .75em; flex-grow: 1"
          @click="onAddHandler">
          NEW PLAN
        </b-button>
      </div>
      <div class="column">
        <form id="usersFilter">
          <section class="modern-form-group">
            <div class="modern-input-controlled">
              <ModernInput  placeholder="Search..."
                            name="searchText"
                            v-model.trim="searchText"
                            type="search"/>
              <div class="modern-input-control">
                <button class="modern-button" @click.prevent="doSearch">
                  <i class="mdi mdi-magnify"></i>
                </button>
              </div>
            </div>
          </section>
        </form>
      </div>
      <div class="column is-2 btn-flex sm-hide"
           style="align-content: end; min-width: 180px; gap: .75em">
        <b-button
          v-if="authAuthenticator(PLAN_MODIFY)"
          class="button-right modern-button"
          type="outlined"
          position="is-right"
          style="max-width: 160px; width: 160px; margin-top: .75em; flex-grow: 1"
          @click="onAddHandler">
          NEW PLAN
        </b-button>
      </div>
    </div>
    <section class="data-grid" style="padding-bottom: 2em">
      <template v-if="!data.length">
        <b-table
          class="modern-table"
          height="100%"
          :columns="columns"
          :loading="loading"
        >
          <template #header="{ column }">
            <div :class="[{'top-sticked': true, 'm-centered': column['centered']},
            column['thclass'],
         'modern-table-header-label']">
              {{ column['label'] }}
              <template v-if="column['meta']">
                <reports-table-tooltip :content="column['meta']" />
              </template>
            </div>
          </template>

          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
          <span class="icon">
            <i class="fal fa-frown fa-3x"></i>
          </span>
              <p>Nothing here</p>
            </div>
          </template>
        </b-table>
      </template>
      <template v-else>
        <ModernTable
          ref="table"
          class="modern-table"
          height="100%"
          :loading="loading"
          :per-page.sync="perPage"
          :total="total"
          :current-page.sync="page"
          :data="data"
          paginated
          hoverable
          pagination-size="is-medium"
          backend-pagination
          backend-sorting
          :defaultSort="defaultSort"
          :default-sort-direction="defaultSortDirection"
          @sort="onSortHandler"
          @page-change="onPageChangeHandler"
        >
          <template #header="{ column }">
            <div :class="[{'top-sticked': true, 'm-centered': column['centered'] },
             column['thclass'], 'modern-table-header-label']"
                 :ref="column['field']">
              {{ column['label'] }}
              <template v-if="column['meta']">
                <reports-table-tooltip :content="column['meta']" />
              </template>
            </div>
          </template>
          <template #default="{ row }">
            <b-table-column field="ids" label="" width="0" cell-class="cell-costil"
                            header-class="costil" sticky>
            </b-table-column>
            <b-table-column field="id" label="ID" width="100" centered sortable>
              {{ row.id }}
            </b-table-column>
            <b-table-column field="name" width="600" label="Name" sortable>
              <span :title="row.name">{{ row.name }}</span>
            </b-table-column>
            <b-table-column field="stripeId" width="250" label="Stripe ID" sortable>
              {{ row.stripeId }}
            </b-table-column>
            <b-table-column centered field="price" width="150" label="Price" sortable>
              {{ row.price | currency(2)  }}
            </b-table-column>
            <b-table-column field="associatedStripeId" width="250"
                            label="Associated Stripe ID" sortable>
              {{ row.associatedStripeId }}
            </b-table-column>
            <b-table-column field="installmentIntervalsCount" label="Installment Intervals Count"
                            width="150" centered sortable>
              {{ row.installmentIntervalsCount }}
            </b-table-column>
            <b-table-column field="opportunities" label="Opportunities"
                            width="180" centered sortable>
              {{ row.getOpportunities() }}
            </b-table-column>
            <b-table-column field="tier" label="Tier" width="120" centered sortable>
              {{ row.tier }}
            </b-table-column>
            <b-table-column field="description" width="250" label="Description">
              <div class="non-action-column"
                   v-if="row.description"
                   style="width: 200px;">
              <span :title="row.getDescription()">{{ row.getShortDescription() }}</span>
              </div>
            </b-table-column>
            <b-table-column width="180" class="action-column"
                            v-if="authAuthenticator(PLAN_MODIFY)">
              <div>
            <span class="icon is-success action-button" title="Edit Plan"
                  @click="onEditHandler(row)">
                <i class="fas fa-pencil-alt fa-lg"></i>
            </span>
                <span class="icon is-success action-button" title="Delete plan"
                      @click="onRemoveHandler(row)">
                  <i class="fas fa-trash-alt fa-lg"></i>
            </span>
              </div>
            </b-table-column>
          </template>
          <template #bottom-left>
            <div class="modern-input-labeled">
              <label class="per-page" for="paymentStatus"
                     style="font-weight: 700; white-space: nowrap">
                Per page
              </label>
              <ModernSelect
                style="margin-right: 1em"
                v-model="perPage"
                :options="perPageOptions"
                @input="perPageChange"
              />
            </div>
          </template>

          <template #empty v-if="!loading">
            <div style="padding-top: 1em; overflow: hidden; text-align: center">
              <span class="icon">
                <i class="fal fa-frown fa-3x"></i>
              </span>
              <p>Nothing here</p>
            </div>
          </template>
        </ModernTable>
      </template>
    </section>
  </div>
</template>

<style lang="stylus" scoped>
@require "../../../assets/style/modern/variables.styl";
::v-deep .m-centered
  text-align center
  margin-right unset

::v-deep .cell-costil
  padding 0 !important
::v-deep .costil
  width 0
  padding 0

td
  overflow hidden
  text-overflow ellipsis
.fix-buttons.buttons:last-child
  margin-bottom 0 !important

.btn-flex
  display: flex;
  justify-content: end
  @media screen and (max-width: $breakpoints.md)
    width 100%
    justify-content: end
    .modern-button
      max-width 160px;

@media (max-width: $breakpoints.md)
  .modern-input-controlled
    padding-top 0
  .modern-input-labeled, .modern-input-controlled
    margin-right 0
  .sm-hide
    display none !important
  .sm-show
    display flex !important

.action-column
  text-align right
  width 100px

.non-action-column
  text-overflow ellipsis
  overflow hidden

.action-column
  text-align right

.action-column .icon
  color $colors.is-success
  display inline-block
  margin 5px 1em
  &:hover
    color $colors.is-success-alt

.fa-pencil-alt, .fa-trash-alt
  cursor pointer

.action-button
  display inline-block
  margin 0 .5rem

</style>
